/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import type {
  AnsweredValue,
  ColumnChoice,
  ColumnMapping,
  ExternalValue,
  ImportPreview,
  ImportSettings,
  UnresolvedValue,
} from '../types';

/**
 * The answers that keep a name as a broker outside the agency carry this
 * prefix, followed by the member who answers for it (empty: nobody). Any
 * other answer says the name is that member.
 */
export const EXTERNAL_PREFIX = 'external:';

/** Whether an answer keeps the name as an outside broker. */
export function isExternalAnswer(answer: string | null): answer is string {
  return answer !== null && answer.startsWith(EXTERNAL_PREFIX);
}

/** The name's own fields, as the panel lists and sends them. */
export type NameEntry = Omit<UnresolvedValue, 'kind'> & { kind?: UnresolvedValue['kind'] };

/**
 * A name the person just answered (or took back) on this screen.
 *
 * Only for the optimistic moment between the click and the server's reply:
 * the server echoes every stored answer in `answered_values`, so after a
 * reload the panel rebuilds from the preview alone. `answer: null` is a
 * local undo the reply has not confirmed yet.
 */
export interface NameAnswer extends NameEntry {
  columnIndex: number;
  /** The member value chosen; `''` means "leave without a broker"; `null` undoes. */
  answer: string | null;
}

/** Local answers, keyed by {@link answerKey}. */
export type NameAnswers = Record<string, NameAnswer>;

/** One line of the panel: a name, its row count, and the answer if any. */
export interface NameLine extends NameEntry {
  answer: string | null;
  /** True for a name kept as a broker outside the agency. */
  external?: boolean;
}

/** The i18n key of the empty choice, whatever label the server sent. */
export const NO_BROKER_LABEL_KEY = 'people.noBroker';

/**
 * The column's choices with "leave without a broker" in the screen's
 * language. The server marks that choice by its value (`''`), not by its
 * label, which is wire copy in pt-BR.
 *
 * @param choices - The column's options.
 * @param t - The spreadsheets namespace translator.
 * @returns The choices, the empty one relabelled.
 */
export function localizedChoices(choices: ColumnChoice[], t: TFunction): ColumnChoice[] {
  return choices.map((choice) => (
    choice.value === '' ? { ...choice, label: t(NO_BROKER_LABEL_KEY) } : choice
  ));
}

/** The key one column's name is stored under locally. */
export function answerKey(columnIndex: number, key: string): string {
  return `${columnIndex}:${key}`;
}

/**
 * The PATCH body that answers one name, or (with `null`) takes the answer back.
 *
 * Only that one value travels: the backend merges per value, so sending the
 * whole map would be both redundant and a race with any answer in flight.
 *
 * @param columnIndex - The sheet column the name lives in.
 * @param value - The spelling shown on screen; the backend folds it.
 * @param answer - The member value, `''` for none, or `null` to undo.
 * @returns The settings patch.
 */
export function valueOverridePatch(
  columnIndex: number,
  value: string,
  answer: string | null,
): ImportSettings {
  if (isExternalAnswer(answer)) {
    return { external_broker_responsibles: { [value]: answer.slice(EXTERNAL_PREFIX.length) } };
  }
  return { value_overrides: { [columnIndex]: { [value]: answer } } };
}

/**
 * The choices for a name kept as an outside broker: keep it with nobody or
 * with a member answering for it, or say it is that member after all.
 *
 * @param choices - The column's own options (members, and "no broker").
 * @param t - The spreadsheets namespace translator.
 * @returns The choices, outside-broker answers first.
 */
export function externalChoices(choices: ColumnChoice[], t: TFunction): ColumnChoice[] {
  const members = choices.filter((choice) => choice.value !== '');
  return [
    { value: EXTERNAL_PREFIX, label: t('people.externalNobody') },
    ...members.map((member) => ({
      value: `${EXTERNAL_PREFIX}${member.value}`,
      label: t('people.externalWith', { name: member.label }),
    })),
    ...members.map((member) => ({ value: member.value, label: t('people.isMember', { name: member.label }) })),
  ];
}

/** The local answers with one name answered, or undone on `null`. */
export function withAnswer(
  answers: NameAnswers,
  columnIndex: number,
  entry: NameEntry,
  answer: string | null,
): NameAnswers {
  const id = answerKey(columnIndex, entry.key);
  return { ...answers, [id]: { ...toEntry(entry), columnIndex, answer } };
}

/** Just the name's own fields, whatever else the entry carried. */
function toEntry(entry: NameEntry): NameEntry {
  return { key: entry.key, value: entry.value, count: entry.count, kind: entry.kind };
}

/** What the server says about one column: open names and stored answers. */
function serverLines(column: ColumnMapping): NameLine[] {
  const open = (column.unresolved_values ?? []).map((entry) => ({ ...entry, answer: null }));
  const answered = (column.answered_values ?? []).map((entry: AnsweredValue) => ({ ...entry }));
  return [...open, ...answered, ...(column.external_values ?? []).map(externalLine)];
}

/** An outside broker as a panel line, answered from the start: keeping the name is the default. */
function externalLine(entry: ExternalValue): NameLine {
  return {
    key: entry.key, value: entry.value, count: entry.count, external: true,
    answer: `${EXTERNAL_PREFIX}${entry.responsible ?? ''}`,
  };
}

/** The local answers given for one column, as panel lines. */
function localLines(columnIndex: number, answers: NameAnswers): NameLine[] {
  return Object.values(answers)
    .filter((entry) => entry.columnIndex === columnIndex)
    .map(({ columnIndex: _column, ...line }) => {
      void _column;
      return line;
    });
}

/**
 * Every name the panel lists for one column, open and answered, most rows
 * first so the answer that fixes the most lines is the first one offered.
 *
 * The server's lines are the truth; a local answer only overrides its own
 * name until the reply catches up.
 *
 * @param column - A person column from the preview.
 * @param answers - The answers given on this screen, not yet confirmed.
 * @returns The lines, sorted by count, then by key.
 */
export function nameLines(column: ColumnMapping, answers: NameAnswers): NameLine[] {
  const byKey = new Map<string, NameLine>();
  [...serverLines(column), ...localLines(column.index, answers)]
    .forEach((line) => byKey.set(line.key, { ...byKey.get(line.key), ...line }));
  return [...byKey.values()]
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/** "3 de 10 nomes resolvidos", as numbers. */
export function nameProgress(lines: NameLine[]): { resolved: number; total: number } {
  return {
    resolved: lines.filter((line) => line.answer !== null).length,
    total: lines.length,
  };
}

/**
 * The columns that get a names panel: person columns with anything to show,
 * either a name still open or one answered (so it can be undone).
 *
 * @param preview - The preview on screen.
 * @param answers - The answers given on this screen.
 * @returns The columns, in sheet order.
 */
export function columnsWithNames(
  preview: ImportPreview | null,
  answers: NameAnswers,
): ColumnMapping[] {
  if (!preview) return [];
  return preview.columns.filter((column) => (
    column.unresolved_values !== undefined && nameLines(column, answers).length > 0
  ));
}
